<template>
  <div class="full-height" v-loading="mitigatedTrailsReqStatus === 'loading'">
    <div v-if="mitigatedTrailsReqStatus === 'saved'">
      <table-toolbar
          :key="trailInfoCollection.key"
          v-if="visibleTrailColumns"
          :search-properties="visibleTrailColumns"
          :total-search-properties="trailColumns"
          :search-data="trailInfoCollection"
          :initial-tags="trailInfoCollection.searchTags"
          :reload-component="trailInfoCollection.reloadToolbar"
          :active-filter-data="activeFiltersData"
          :disable-search-box="disableSearchBox"
          @update-querydata="updateTableQueryData"
          @refresh-tabledata="refreshTableData"
          @update-columns-data="modifyColumnsData"
          @add-searchtags="addSearchTags"
          @deactivate-filter="deactivateFilter"
      >
        <div slot="left-toolbar-tools" class="filter-button-group">
          <span v-if="filterList.length">
            <div class="display-inline mr-18 relative-pos" v-for="(btn, index) in filterList" :key="index">
              <el-input
                  ref="customFilterInput"
                  class="filter-input"
                  v-if="btn.editFlag"
                  placeholder="+Name"
                  v-model="btn.name"
                  @change.native.stop="handleFilterNameChange(btn)"
                  @keyup.native.enter="handleFilterNameChange(btn)"
                  @blur="handleFilterNameChange(btn)"
              ></el-input>
              <el-button
                  v-else
                  size="small"
                  round
                  :class="{'active': btn.active}"
                  :id="btn.id"
                  @click="changeState(btn)"
              >
                {{ btn.name }}
                <el-tooltip class="item" effect="dark" content="Edit or Delete" placement="bottom">
                <span>
                  <i class="el-icon-edit el-icon--right" @click.stop="editFilter(btn)"></i>
                  <i class="el-icon-close el-icon--right" @click.stop="removeBtnFromList(btn, index)"></i>
                </span>
                </el-tooltip>
              </el-button>

              <span v-if="btn.errFlag" class="filter-name-validator">{{ filterErrMsg }}</span>
            </div>
          </span>
        </div>
        <div slot="search-actions" class="search-actions-flex">
          <xm-button class="reopen-button" :disabled="disabledMitigateSelectedProgressions"
                     @button-click="openMitigateDialog"
                     size="medium" label="Reopen"
          >
          </xm-button
          >
          <date-range-dropdown
              :disable-selection="isTrailsLoading"
              :store-pref-constant="daterangeUserPrefConstant"
              :date-range-val="trailProgressionDateRangeUserSetting"
          ></date-range-dropdown>
        </div>
      </table-toolbar>
    </div>

    <multi-select-action-table-element
        v-if="mitigatedTrailsReqStatus === 'saved'"
        ref="attacksTable"
        :columns="visibleTrailColumns"
        :table-data="trailInfoCollection"
        :height-offset="40"
        :actions="tableActions"
        @update-record-count="recordCountHandler"
        @table-selection-change="canReopenMitigateSelectedProgressions"
    ></multi-select-action-table-element>

    <el-dialog style="text-align:center;" :visible.sync="openSuccessDialogue">
      <div>
        <div class="success-header">
          Success!
        </div>
        <div>
          <div style="text-align:center">
            <img v-once src="/static/images/jira-success-logo.svg" alt="logo">
          </div>
          <div v-if="checkForSuccessBox" style="overflow-y:auto" class="successful-reopens">
            <ul class="successful-reopens-list">
              <li v-for="rec of mitigatedProgressionObject" :key="rec.trailID">
                Trail ID:
                <router-link :to="{ name: 'attack-track-details', params: { trailId: rec.trailID }}"
                >{{ rec.trailIDLabel }}
                </router-link>
              </li>
            </ul>
          </div>
        </div>

        <div class="goto-jira-btn" v-if="!checkForSuccessBox">
          <div>
            <a class="style-goto-jira"
               :href="jiraTicketDetails.jira_ticket_link"
               target="_blank"
            ><span>Go to Jira</span>
            </a>
          </div>
        </div>
      </div>
    </el-dialog>
    <el-dialog title="Reopen the Selected Progression(s)" :visible.sync="openReopenMitigateDialogVisible">
      <el-form>
        <label>
          {{selectedProgressions.length > 1 ?  "Are you sure you want to reopen the following progressions?" : "" +
            "Are you sure you want to reopen this progression?" }}
        </label>

        <h6><br/>{{ selectedProgressions.length > 1 ? "Progressions selected:" : "Progression selected:"}}</h6>
        <span v-for="(progression, idx) in selectedProgressions" :key="idx">
          {{ progression.trailIdLabel }}
          <el-divider v-if="idx !== selectedProgressions.length - 1" direction="vertical"></el-divider>
        </span>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button
            @click="
            openReopenMitigateDialogVisible = false
            disabledMitigateSelectedProgressions = false
          "
        >Cancel</el-button
        >
        <el-button type="primary" @click="reopenMitigatedSelectedProgressions">Confirm</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {
  MITIGATED_TRAILS_REQUEST,
  MITIGATED_TRAILS_SUCCESS,
  MITIGATED_PROGRESSION_VIEW_PREFERENCES_REQ,
  SAVE_MITIGATED_PROGRESSION_VIEW_PREFERENCES
} from '@/store/modules/trails/trailsinfo.type'
import {PREFERENCE_STORE_REQ} from '@/store/modules/column-preference/column-preference.type'
import {USER_LIST_REQUEST, USER_LIST_SUCCESS} from '@/store/modules/user/user.type'
import {THREAT_PROGRESSION_DATE_RANGE_PREFERENCE} from '@/store/modules/user_pref/user-pref.type'
import {JIRA_ALL_TICKETS} from '@/store/modules/jira/jira-setting.type'

import {mitigatedTrailsMetaInfo} from '@/column-header/trail/trails-mitigated-meta'

import PageHeaders from '@/components/layout/PageHeaders.vue'
import LineChartJs from '@/components/chart/LineChartJs.vue'
import DonutChartJs from '@/components/chart/DonutChartJs.vue'
import DonutholeChartLabelPlugin from '@/components/chart/plugins/donuthole-chart-label.plugin'
import ActionTableElement from '@/components/table/ActionTableElement.vue'
import TableToolbar from '@/components/table/TableToolbar.vue'
import DateRangeDropdown from '@/components/dropdown/DateRangeDropdown.vue'
import customFilterMixin from '@/mixins/customFilterMixin'
import MultiSelectActionTableElement from "../../../components/table/MultiSelectActionTableElement.vue";
import {
  BULK_TRAIL_REOPEN_MITIGATED_REQ
} from "../../../store/modules/trails/trailsinfo.type";
import {
  JIRA_FETCH_TICKETS,
  JIRA_TICKET_STATUS_CHANGE
} from "../../../store/modules/jira/jira-setting.type";
import XmButton from "../../../components/button/XMButton.vue";

export default {
  name: 'MitigatedCyberAttacks',
  mixins: [customFilterMixin],
  data() {
    return {
      mitigatedProgressionColumnPrefs: {},
      tableName: "Progression_Mitigated",
      daterangeUserPrefConstant: THREAT_PROGRESSION_DATE_RANGE_PREFERENCE,
      breadcrumbs: [{title: 'Threats'}, {title: 'Mitigated'}],
      trailColumns: mitigatedTrailsMetaInfo.meta.columns,
      visibleTrailColumns: [],
      trailInfoCollection: {},
      selectedAttackTrail: {},
      openAssignToDialogVisible: false,
      tableActions: [],
      tblId: 5,
      openReopenMitigateDialogVisible: false,
      disabledMitigateSelectedProgressions: true,
      selectedProgressions: [],
      openSuccessDialogue: false, // for success screen after ticket creation.
      checkForSuccessBox: false,
      mitigatedProgressionObject: [],
      disableCheckboxOnPopUp: false
    }
  },
  methods: {
    openMitigateDialog() {
      this.selectedProgressions = this.$refs.attacksTable.rowsSelected
      this.openReopenMitigateDialogVisible = true
    },
    getTrailListInfo(tableMetaData) {
      this.$store.dispatch(MITIGATED_TRAILS_REQUEST, {dateRangeSel: this.trailProgressionDateRangeUserSetting}).then(
        trails => {
          this.trailInfoCollection = trails
          this.$store.dispatch(MITIGATED_TRAILS_SUCCESS)
          this.setTableMetaData(tableMetaData)
          this.fetchJiraTickets();
        },
        errMsg => {
          console.log(errMsg)
        }
      )
    },
    gotoDetails(trailId) {
      this.$router.push({
        name: 'attack-track-details',
        params: {trailId: trailId}
      })
    },
    updateTableQueryData() {
      if (this.$refs.attacksTable) {
        this.$refs.attacksTable.updateTableData()
      }
    },
    recordCountHandler() {
      // key is used to re-render TableToolbar component
      // on page change to update 'queryCountText'
      this.trailInfoCollection.key = !this.trailInfoCollection.key
      this.trailInfoCollection.reloadToolbar = false
    },
    modifyColumnsData(selectedColumns) {
      this.mitigatedProgressionColumnPrefs.userId = this.userProfile.id;
      this.mitigatedProgressionColumnPrefs.tblName = this.tableName;
      this.mitigatedProgressionColumnPrefs.preferedCols = selectedColumns.length === 0 ? NaN : selectedColumns;
      this.mitigatedProgressionColumnPrefs.tblId = 5;

      this.visibleTrailColumns = this.trailInfoCollection.updateVisibleColumns(this.trailColumns, selectedColumns)

      this.$store.dispatch(SAVE_MITIGATED_PROGRESSION_VIEW_PREFERENCES, this.mitigatedProgressionColumnPrefs).then(
        data => {
          // store preferences in vuex
          this.mitigatedProgressionColumnPrefs.preferedCols = this.visibleTrailColumns;
          this.$store.dispatch(PREFERENCE_STORE_REQ, this.mitigatedProgressionColumnPrefs);
        })
    },
    callApi(tableMetaData) {
      if (this.usersListStatus !== 'saved') {
        this.$store.dispatch(USER_LIST_REQUEST).then(data => {
          this.$store.dispatch(USER_LIST_SUCCESS)
        })
      }

      this.getTrailListInfo(tableMetaData)
    },
    setTableMetaData(tableMetaData) {
      this.trailInfoCollection.searchTags = tableMetaData.searchTags
      this.trailInfoCollection.currentPage = tableMetaData.activePage
    },
    refreshTableData(tableMetaData) {
      this.callApi(tableMetaData)
    },
    loadMitigatedProgressionColumnPreferences() {
      // check if already present in store
      if (_.has(this.userPref, this.userProfile.id) && _.has(this.userPref[this.userProfile.id], this.tableName)) {
        //  read values from store
        this.visibleTrailColumns = this.userPref[this.userProfile.id][this.tableName]
        // this.updateStoreWithPrefs()
      } else {
        // fetch from database
        this.$store.dispatch(MITIGATED_PROGRESSION_VIEW_PREFERENCES_REQ, this.userProfile.id)
          .then(data => {
            this.visibleTrailColumns = this.trailColumns.filter(col => {
              return this.mitigatedProgressionPreferences.includes(col.label) && col.label !== "State"
            })
            this.updateStoreWithPrefs()
          })
      }
    },
    updateStoreWithPrefs() {
      this.mitigatedProgressionColumnPrefs = {
        userId: this.userProfile.id,
        tblName: this.tableName,
        preferedCols: this.visibleTrailColumns
      }

      this.$store.dispatch(PREFERENCE_STORE_REQ, this.mitigatedProgressionColumnPrefs);
    },
    canReopenMitigateSelectedProgressions() {
      this.disabledMitigateSelectedProgressions = this.$refs.attacksTable.rowsSelected.length <= 0;
    },
    reopenMitigatedSelectedProgressions() {
      this.checkForSuccessBox = false
      this.disabledMitigateSelectedProgressions = true
      let email = this.userProfile.email
      let trailIds = []
      let trailIdLabels = []
      this.$refs.attacksTable.rowsSelected.forEach(trail => {
        trailIds.push(trail.id)
        trailIdLabels.push(trail.trailIdLabel)
      })

      this.$store
        .dispatch(BULK_TRAIL_REOPEN_MITIGATED_REQ, {
          trails: trailIds,
          assignFrom: email,
          assignTo: email
        })
        .then(() => {
          this.disabledMitigateSelectedProgressions = false
          this.openReopenMitigateDialogVisible = false
          this.$store.dispatch(JIRA_FETCH_TICKETS).then(_ => {
            for (let trail of trailIds) {
              if (this.fetchTickets.includes(trail)) {
                let payload = {
                  trail_id: trail,
                  status_type: "In Progress",
                  comment_for_ticket: 'Trail has been reopened'
                }
                this.$store.dispatch(JIRA_TICKET_STATUS_CHANGE, payload)
              }
            }
            this.mitigatedProgressionObject = []
            let temp = []
            for (let id of trailIds) {
              if (this.fetchTickets.includes(id)) {
                for (let ticket of this.jiraAllTickets) {
                  if (ticket.trail_id === id) {
                    temp.push(ticket.jira_ticket_link)
                    break;
                  }
                }
              } else {
                temp.push('N/A') // Storing N/A inside array if ticket being not created
              }
            }
            for (let cnt = 0; cnt < trailIds.length; cnt++) {
              let obj = {
                'trailID': trailIds[cnt],
                'ticketLink': temp[cnt],
                'trailIDLabel': trailIdLabels[cnt]
              }
              this.mitigatedProgressionObject.push(obj);
            }
            this.checkForSuccessBox = true // For checking whether a single progression or list of progression selected
            this.openSuccessDialogue = true
          })
          this.refreshTableData({
            activePage: 1,
            searchTags: this.trailInfoCollection.searchTags
          })
        }, errMsg => {
          this.disabledMitigateSelectedProgressions = false
          this.openReopenMitigateDialogVisible = false
        })
    },
    fetchJiraTickets() {
      this.$store.dispatch(JIRA_ALL_TICKETS).then(() => {
        let allTicketIds = []
        for (let i of this.jiraAllTickets) {
          allTicketIds.push(i.trail_id);
        }
        for (let entry of this.trailInfoCollection.models) {
          if (allTicketIds.includes(entry.id)) {
            let currentTrail = this.jiraAllTickets.filter(rec => rec.trail_id === entry.id)
            entry.jiraTicketLink = currentTrail[0].jira_ticket_link
            entry.jiraTicketId = currentTrail[0].ticket_key
            entry.stringify += entry.jiraTicketId.toLowerCase()
          } else {
            entry.jiraTicketLink = ""
            entry.jiraTicketId = "N/A"
          }
        }
      })
    }
  },
  created() {
    let tableMetaData = {
      activePage: 1,
      searchTags: []
    }
    this.callApi(tableMetaData)
    this.loadMitigatedProgressionColumnPreferences()
  },
  watch: {
    trailProgressionDateRangeUserSetting: {
      handler: function (newDateRange, oldDateRange) {
        if (newDateRange !== oldDateRange) {
          this.refreshTableData({
            activePage: 1,
            searchTags: this.trailInfoCollection.searchTags
          })
        }
      }
    }
  },
  computed: {
    isTrailsLoading() {
      return this.mitigatedTrailsReqStatus === 'loading'
    },
    ...mapGetters([
      'mitigatedTrailsReqStatus',
      'mitigatedTrailsRecords',
      'userProfile',
      'usersList',
      'usersListStatus',
      'trailProgressionDateRangeUserSetting',
      'mitigatedProgressionPreferences',
      'userProfile',
      'userPref',
      'jiraAllTickets',
      'jiraTicketDetails',
      'fetchTickets'
    ]),
    lineChartOptions() {
      return {
        layout: {
          padding: {
            left: 10,
            right: 10
          }
        },
        tooltips: {
          enabled: true
        },
        elements: {
          point: {
            pointStyle: 'line'
          },
          line: {
            fill: true,
            backgroundColor: 'rgb(242, 248, 255)',
            borderColor: 'rgb(0, 112, 249)'
          }
        }
      }
    },
    donutLabelPlugin() {
      return DonutholeChartLabelPlugin
    },
    donutOptions() {
      return {
        cutoutPercentage: 80,
        rotation: 0.8 * Math.PI,
        circumference: 1.4 * Math.PI,
        plugins: {
          'donuthole-chart-label-plugin': {
            style: {
              circleFillColor: '#eff0f2',
              textfillColor: '#2e3344'
            }
          }
        }
      }
    }
  },
  components: {
    XmButton,
    MultiSelectActionTableElement,
    PageHeaders,
    LineChartJs,
    DonutChartJs,
    ActionTableElement,
    TableToolbar,
    DateRangeDropdown
  }
}
</script>

<style scoped>
@import '../../../assets/styles/_variables.css';

.el-col {
  padding-bottom: 8px;
}

.el-card {
  padding-top: 5px;
}

.trails-info {
  max-width: 65%;

  > span {
    display: block;
    padding: 2px;
  }
}

.title {
  font-size: 0.9em;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.info {
  font-size: 0.8em;
}

.trail-icon {
  transform: rotate(90deg);
}

.material-icons {
  font-size: 2em;
  margin-left: 10px;
}

.el-dropdown-menu.el-popper {
  .material-icons {
    font-size: 1em;
    margin-left: 0px;
  }
}

.el-card__body {
  padding: 0 !important;

  > div {
    margin: 5px;
  }
}

.circle {
  width: 24px;
  height: 24px;
  display: table-cell;
  border-radius: 50%;
  font-size: 15px;
  color: #fff;
  line-height: 24px;
  text-align: center;
  background: var(--primary-blue-400);
}

.el-button--mini {
  padding: 5px;
}

.view-mode {
  height: 40px;
  width: 100px;
  vertical-align: middle;
  line-height: 40px;

  .el-select:before {
    content: '\e896';
    font-family: 'Material Icons';
    font-size: 20px;
    margin-right: 16px;
    color: var(--gray-500);
    left: 50px;
    position: relative;
  }

  >>> .el-select {
    height: inherit;

    .el-input.el-input--suffix {
      visibility: hidden;
      top: -45px;
    }
  }
}

.bottom {
  border-top: 1px solid #f0f0f0;
}

.clearfix:before,
.clearfix:after {
  display: table;
  content: '';
}

.clearfix:after {
  clear: both;
}

.display-none {
  display: none;
}

.filter-button-group {
  .el-button {
    background-color: white;
    color: var(--gray-800);
    margin-bottom: 25px;

    &.active {
      background-color: var(--denim-600);
      color: white;
    }
  }

  >>> .el-input {
    input {
      border-radius: 20px;
      width: 102px;
      height: 37px;
    }
  }
}

.filter-input {
  display: table-cell;
}

>>> .flex.gaps:not(.column):not(.reverse) > :not(:last-child) {
  margin-right: var(--flex-gap, 1em);
}

.filter-name-validator {
  position: absolute;
  top: 34px;
  left: 3px;
  color: red;
  width: 110px;
}

.relative-pos {
  position: relative;
}

.search-actions-flex {
  display: flex;
  flex-wrap: nowrap;
  width: max-content;
}

.reopen-button {
  margin-right: 10px;
}

>>> .vue-tags-search-box {
  width: 320px;
}

>>> .search-box-container {
  position: relative;
  right: 300px;
}

.success-header {
  margin-bottom: 20px;
}

.successful-reopens {
  display: flex;
  justify-content: center;
}

.successful-reopens-list {
  padding-inline-start: 0;
}

</style>



// WEBPACK FOOTER //
// src/views/monitor/cyber_attacks/MitigatedCyberAttacks.vue